import { Skill } from "../types/prisma-enums";
import type { GameContent, Item, ItemMode, Level } from "./types";
import type { Localized } from "../i18n";
import type { CurriculumBand } from "./vocabularyBands";

/**
 * Letter Lagoon — Alphabets & Words · games-spec.md §5.
 *
 * A shallow lagoon of stepping stones, each carrying one letter. Turo says a
 * letter name or its sound, and the child **steps or hops onto that stone** —
 * the whole body selects, not a cursor. From L4 the child **walks the stones in
 * sequence, left to right**, to build a word.
 *
 * 🔴 THAT SEQUENCING IS WHY THIS GAME EARNS ITS CATEGORY rather than merely
 * hosting it: *"directionality is itself an early-literacy competency, and here
 * the child practises it by physically travelling left to right rather than by
 * watching a cursor do it."*
 *
 * 🔴 THIS DOES NOT BREAK "NO READING REQUIRED". The invariant is that a child
 * never has to read in order to **operate** the product. Here letters are the
 * **content being taught**, never the interface: every prompt is spoken, every
 * stone carries a letter *and* a picture cue, and no child is ever required to
 * read a word to reach the next screen. Both properties are asserted, not
 * described.
 *
 * ⚠️ THIS TABLE WAS BILINGUAL AND IS NOT ANY MORE (ADR-044). The Malayalam
 * prompts went, and so did the Malayalam entries in `CONFUSABLE_PAIRS` below —
 * the letters this game teaches are Latin, and a confusable pair in a script it
 * never shows is a constraint on nothing.
 */

/**
 * 🔴 LETTERS A 4-YEAR-OLD CONFUSES — games-spec.md §5's accessibility note.
 *
 * *"The letters most often confused at this age — b/d/p/q ... must never be the
 * only distinguishing feature of two stones in the same round"*, and must be
 * kept out of the same choice set **until L5**.
 *
 * A table plus a test, rather than care while authoring, because this is exactly
 * the constraint a well-meaning content edit breaks six months from now — and it
 * breaks invisibly, presenting as a child who "cannot do letters".
 *
 * ⚠️ THE MALAYALAM CONSONANT PAIRS WENT WITH THE LANGUAGE (ADR-044). The rule's
 * source text named them alongside b/d/p/q; this game teaches Latin letters, so
 * a pair in a script it never shows constrains nothing.
 */
export const CONFUSABLE_PAIRS: readonly (readonly [string, string])[] = [
  ["b", "d"],
  ["b", "p"],
  ["d", "p"],
  ["p", "q"],
  ["d", "q"],
  ["b", "q"],
  ["m", "n"],
];

/**
 * 🔴 THE CUE WORD FOR EACH LETTER, AND EVERY ONE COMES FROM SINDOORI'S POOLS.
 *
 * `asset` is the cue picture's id and it is also the *word*, which is why this
 * table exists rather than the ids being written inline: the pool rule
 * (`vocabularyBands.ts`) is checked against these values, and one map is far
 * easier to audit than thirty-six scattered strings.
 *
 * ⚠️ FIVE OF THE ORIGINAL TWELVE CUE WORDS WERE NOT IN ANY POOL, AND THEY WERE
 * NOT WRONG SO MUCH AS OLDER THAN THE RULE. `sun`, `orange`, `ice`, `rain` and
 * `egg` predate Sindoori's bands. Corrected here:
 *
 *     sun    → star     (B1)
 *     rain   → river    (B2)
 *     egg    → eraser   (B2)
 *     orange → orchestra (B4)   ⚠️ see below
 *     ice    → instrument (B4)  ⚠️ see below
 *
 * 🔴 AND TWO LETTERS HAVE NO AGE-APPROPRIATE CUE AT ALL — A REAL GAP IN THE
 * POOLS, NOT A GAP IN THIS TABLE. Across all four bands the ONLY word beginning
 * with `o` is `orchestra` and the only one beginning with `i` is `instrument`,
 * both B4 (ages 5.5–6). Neither is a picture a three-year-old can name, and at
 * L1 the cue is exactly that — a picture a three-year-old names.
 *
 * The rule is followed rather than quietly broken: the B4 words are used and
 * tagged B4. But this is on the review packet for Sindoori (plan D2) as a
 * request rather than a question — **the pools need an early-band noun starting
 * with `o` and one starting with `i`**. Inventing "orange" and "ice" ourselves
 * is exactly what the rule exists to prevent, however obvious they look.
 *
 * Their consequence is already applied below: `o` and `i` are dropped from L3,
 * where the cue word *is* the question, and replaced by `ball` and `cup`.
 */
export const CUE_WORDS: Readonly<
  Record<string, { word: string; band: CurriculumBand }>
> = {
  m: { word: "mango", band: "B1" },
  a: { word: "apple", band: "B1" },
  s: { word: "star", band: "B1" },
  t: { word: "tree", band: "B1" },
  k: { word: "kite", band: "B1" },
  f: { word: "fish", band: "B1" },
  l: { word: "leaf", band: "B1" },
  b: { word: "ball", band: "B1" },
  /*
   * ⚠️ `cup` → `cat`, AND THE PICTURE IS WHAT DECIDED IT. The delivered cue
   * sheet (`shared/art/cueAtlas.ts`) draws a cat for `c`, and at L3 the cue word
   * IS the question — *"which letter does cat start with?"* — so a sheet showing
   * a cat while the prompt says "cup" asks about one thing and shows another.
   * Both are B1 pool words, so this trades nothing away: `cat` is, if anything,
   * the more nameable picture at three.
   */
  c: { word: "cat", band: "B1" },
  g: { word: "goat", band: "B2" },
  r: { word: "river", band: "B2" },
  e: { word: "eraser", band: "B2" },
  o: { word: "orchestra", band: "B4" },
  i: { word: "instrument", band: "B4" },
};

/**
 * How each letter's NAME is spelled for the synthesiser — and why it is not
 * simply the letter.
 *
 * 🔴 A BARE LETTER INSIDE AN ENGLISH SENTENCE IS NOT READ AS A LETTER. Handed
 * *"Jump on the letter a"*, a text-to-speech voice reads the `a` as the ARTICLE
 * and a child hears *"jump on the letter uh"*. `i` comes back as "ih" or "eye"
 * depending on the sentence around it, `e` as "uh" or "ee", and there is nothing
 * in the text that chooses. Those are the four levels of this game asking a
 * question with the answer taken out of it, in the one channel a pre-reader has
 * — and it fails silently, because a mispronounced line is still a line that
 * played.
 *
 * 🔴 SO THE VOICE IS GIVEN THE LETTER'S NAME, SPELLED AS A WORD. "em", "ess",
 * "ay" are how the names are pronounced, and they are unambiguous to a
 * synthesiser in a way a single character never is. What is PRINTED is
 * untouched — see `Item.spoken`.
 *
 * ⚠️ `zed`, NOT `zee`, and it is the same decision `CUE_WORDS` makes about which
 * words a child hears. This product is written to the NCF-FS for Indian
 * classrooms; a narrator saying "zee" is teaching an alphabet the child's
 * teacher does not use.
 *
 * ⚠️ ALL TWENTY-SIX, THOUGH THIS GAME TEACHES FOURTEEN. The name of a letter is
 * a fact about English rather than about the current item pool, and a table with
 * holes in it is one content addition away from being silently wrong again.
 */
export const LETTER_NAMES: Readonly<Record<string, string>> = {
  a: 'ay',
  b: 'bee',
  c: 'see',
  d: 'dee',
  e: 'ee',
  f: 'eff',
  g: 'gee',
  h: 'aitch',
  i: 'eye',
  j: 'jay',
  k: 'kay',
  l: 'el',
  m: 'em',
  n: 'en',
  o: 'oh',
  p: 'pee',
  q: 'cue',
  r: 'ar',
  s: 'ess',
  t: 'tee',
  u: 'you',
  v: 'vee',
  w: 'double-you',
  x: 'ex',
  y: 'why',
  z: 'zed',
}

/**
 * The spoken twin of a printed prompt, or `undefined` when it needs none.
 *
 * 🔴 DERIVED FROM THE PRINTED LINE RATHER THAN AUTHORED BESIDE IT. Two hand-kept
 * strings per item is two strings that drift, and the drift is inaudible: the
 * card would read *"jump on the letter g"* while the speaker asked for `f`, and
 * the round would mark a child wrong for answering the question they were shown.
 * One authored sentence, one mechanical substitution.
 *
 * ⚠️ IT ONLY EVER REPLACES A LETTER THE PROMPT ALREADY ENDS ON, so it cannot
 * introduce a word the printed line does not have. A prompt that names no letter
 * — every L3 item, *"Which letter does mango start with?"* — comes back
 * `undefined` and is spoken exactly as written.
 */
function spokenPrompt(en: string): Localized<string> | undefined {
  /*
   * 🔴 EVERY BARE SINGLE LETTER, WHEREVER IT SITS — and it used to be only the
   * one a prompt ENDED on. *"Jump on the letter m"* was the only shape that
   * named a letter until `LETTER_ORDER` arrived with *"What comes after f?"*,
   * where the letter is mid-sentence and is not the target. A tail match found
   * nothing there, so the level that most needs the letter said clearly would
   * have been the one saying *"what comes after uh?"*.
   *
   * ⚠️ A BARE `a` IN THIS GAME IS ALWAYS THE LETTER, NEVER THE ARTICLE, and
   * that is a property of the prompts rather than of English — which is exactly
   * why it is asserted rather than trusted. `letterPronunciation.test.ts` holds
   * every single-letter token in every prompt to being a letter the item is
   * about; write *"Find a leaf"* and it fails there rather than quietly
   * shipping a narrator saying "find ay leaf".
   */
  const spoken = en.replace(
    /\b([a-z])\b/g,
    (whole, letter: string) => LETTER_NAMES[letter] ?? whole,
  )
  return spoken === en ? undefined : { en: spoken }
}

/**
 * Terse constructor.
 *
 * 🔴 `asset` IS REQUIRED HERE, unlike the shared `Item`. A stone showing only a
 * letter would make reading the interface — the one thing the invariant
 * forbids — so every stone carries a picture cue as well.
 *
 * 🔴 THE CUE AND THE BAND COME FROM `CUE_WORDS`, NOT FROM THE CALL SITE. An
 * author writing `asset` by hand is an author who can write a word nobody
 * approved; taking it from the target letter makes the pool rule structural
 * rather than remembered.
 */
const item = (
  id: string,
  en: string,
  audioKey: string,
  target: string,
  choices: string[],
  mode: ItemMode,
  sequence?: string[],
): Item => {
  const cue = CUE_WORDS[target];
  return {
    id,
    prompt: { en },
    /*
     * 🔴 THE ONE PLACE THE VOICE AND THE SCREEN ARE ALLOWED TO DIFFER, and it is
     * derived here so that every letter item gets it — including the ones added
     * six months from now by somebody who never read this file.
     */
    spoken: spokenPrompt(en),
    audioKey,
    target,
    choices,
    asset: cue?.word,
    bandHint: cue?.band,
    mode,
    /*
     * Derived from the shared table rather than written per item, so the two
     * cannot disagree. `CONFUSABLE_PAIRS` stays the source of truth; this is the
     * per-item view of it that a selector can act on without re-deriving the
     * pairs on every draw.
     */
    confusableWith: confusablesOf(target),
    sequence,
  };
};

/** Every letter `target` is confusable with, from the shared pair table. */
function confusablesOf(target: string): string[] {
  return CONFUSABLE_PAIRS.flatMap(([a, b]) =>
    a === target ? [b] : b === target ? [a] : [],
  );
}

/* ── L1 · letter shapes ──────────────────────────────────────────────────────
 * Match the letter you are shown. No sound, no name — pure shape matching,
 * which is where a 3-year-old can meet the alphabet without being asked to
 * remember anything.
 *
 * 🔴 `zones` is CONTENT (how many places to stand), never difficulty, and it is
 * the SAME at every level: a level-3 step is the same step as a level-1 step.
 * What varies is per ITEM — the 2- and 4-stone variants each level carries — so
 * that LL-3's ladder moves a child on their own competence rather than on which
 * world they are in. See the `motorAction` note below.
 */
const L1: Level = {
  level: 1,
  name: { en: "Find the letter" },
  objective: {
    en: "Jump on the stone that matches",
  },
  ncf: ["C-9.3"],
  /*
   * ⚠️ SIX, LIKE EVERY LEVEL OF EVERY GAME. It was ten, the most the twenty
   * authored items could carry under *"pools are ≥ 2× itemCount, so a repeat is
   * not a replay"*; six leaves headroom rather than sitting on that ceiling.
   */
  itemCount: 6,
  /*
   * ⚠️ FOUR, NOT FIVE — AND IDENTICAL AT ALL THREE LEVELS, WHICH IS THE HALF
   * THAT MATTERS.
   *
   * This said `zones: 5` since the table was written, while every item offered
   * three choices and `LagoonRound` declares `stones.length` to the kit — so the
   * number here was never the number a child stood on. LL-0 capped layouts at
   * four (GDD §3.2: five zones *"needs more lateral floor than a classroom
   * gives"*), turning a stale value into a forbidden one.
   *
   * 🔴 THE FIRST ATTEMPT AT THIS SET L1 TO 3 AND L3 TO 4, FOLLOWING GDD §3.2's
   * *"4-zone layout (Level 3 only)"*, AND A TEST CAUGHT IT. `traverse.test.ts`
   * asserts L1's motor action deep-equals L3's, because ADR-031 says motor
   * difficulty never scales with level — and it is right: the 4-zone table puts
   * the outer stone at 1.05 torso units against the 3-zone table's 0.60, so a
   * per-level stone count is a longer walk for reaching level 3. That is the
   * GDD contradicting its own §5 (*"the spec's rule that motor difficulty never
   * scales with level is absolute"*), and where the GDD disagrees with itself
   * the repo invariant settles it.
   *
   * So this is the MAXIMUM an item at any level may offer, and it is the same
   * everywhere. Stone count varies per ITEM — the 2- and 4-stone variants below
   * — which is what LL-3's support ladder moves a child between, on their own
   * competence rather than on which world they are in.
   */
  /*
   * 🔴 TWO, AND IT IS THE ROW THIS LEVEL WAS ALWAYS DESCRIBED AS HAVING. The
   * `centreHome` note in `LagoonRound` opens *"the row is two stones — left and
   * right — and between them is a third floor zone with nothing on it"*, and
   * that has been the design since LL-2. The number here said four, so the pool
   * kept authoring three- and four-choice items that widened the row out from
   * under it whenever a child was doing well.
   *
   * ⚠️ `zones` IS CONTENT — how many places there are to stand — so this is the
   * declaration and `LagoonRound`'s `stoneCapFor` is the enforcement. Both,
   * because `traverse.test.ts` holds items against this number and nothing holds
   * the runtime against it.
   */
  motorAction: { kind: "step", zones: 2 },
  items: [
    item(
      "ll.l1.a",
      "Jump on the letter m",
      "ll_l1_a",
      "m",
      ["m", "a"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.b",
      "Jump on the letter a",
      "ll_l1_b",
      "a",
      ["a", "k"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.c",
      "Jump on the letter s",
      "ll_l1_c",
      "s",
      ["s", "m"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.d",
      "Jump on the letter t",
      "ll_l1_d",
      "t",
      ["t", "a"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.e",
      "Jump on the letter k",
      "ll_l1_e",
      "k",
      ["k", "s"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.f",
      "Jump on the letter o",
      "ll_l1_f",
      "o",
      ["o", "t"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.g",
      "Jump on the letter f",
      "ll_l1_g",
      "f",
      ["f", "a"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.h",
      "Jump on the letter i",
      "ll_l1_h",
      "i",
      ["i", "m"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.i",
      "Jump on the letter l",
      "ll_l1_i",
      "l",
      ["l", "o"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.j",
      "Jump on the letter r",
      "ll_l1_j",
      "r",
      ["r", "e"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.k",
      "Jump on the letter e",
      "ll_l1_k",
      "e",
      ["e", "f"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.l",
      "Jump on the letter g",
      "ll_l1_l",
      "g",
      ["g", "i"],
      "LETTER_SHAPE",
    ),

    /*
     * ── the second run of the pool ───────────────────────────────────────
     *
     * 🔴 THESE WERE THE 2-STONE VARIANTS, AND TWO STONES IS NOW THE WHOLE
     * LEVEL. Every item above offers two letters as well, so there is no
     * narrower variant left to be: the founder's instruction is two ponds and
     * two options everywhere this row is laid out.
     *
     * ⚠️ WHICH LEAVES THE STONE COUNT OUT OF THE SUPPORT LADDER AT L1, and that
     * is the trade. `SUPPORT_RUNGS` used to trim four stones to three to two as
     * a child struggled; at two there is nothing to trim. The ladder's other
     * seven dials — Leo's patience, the cue emphasis, the glow, Leo walking to
     * the stone, Leo telling, target-only — all still apply, and they are what
     * carries support here now.
     *
     * ⚠️ The same targets deliberately reappear. Recycling is Sindoori's
     * instruction: a child meeting `m` a second time should meet a letter they
     * have already seen — and against a DIFFERENT partner, which is what keeps
     * twenty items twenty questions rather than ten listed twice.
     */
    item(
      "ll.l1.m",
      "Jump on the letter m",
      "ll_l1_a",
      "m",
      ["m", "s"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.n",
      "Jump on the letter a",
      "ll_l1_b",
      "a",
      ["a", "t"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.o",
      "Jump on the letter s",
      "ll_l1_c",
      "s",
      ["s", "k"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.p",
      "Jump on the letter t",
      "ll_l1_d",
      "t",
      ["t", "l"],
      "LETTER_SHAPE",
    ),

    /*
     * ── the third run ────────────────────────────────────────────────────
     *
     * 🔴 THESE WERE THE 4-STONE VARIANTS — the low-support rung, gone with the
     * stone count for the reason above. Each keeps a different partner from the
     * set it was authored with (`m` meets `a`, then `s`, then `t`), so the pool
     * is twenty distinct discriminations. The rule is *"pools are ≥ 2×
     * itemCount, so a repeat is not a replay"*; at an itemCount of six the pool
     * clears that with room to spare.
     *
     * ⚠️ NOTHING HERE INVENTS A DISTRACTOR. Every partner is a letter the item
     * already carried, so the `CONFUSABLE_PAIRS` rule is satisfied by
     * construction rather than by re-checking twenty pairs by hand.
     */
    item(
      "ll.l1.q",
      "Jump on the letter m",
      "ll_l1_a",
      "m",
      ["m", "t"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.r",
      "Jump on the letter k",
      "ll_l1_e",
      "k",
      ["k", "l"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.s",
      "Jump on the letter f",
      "ll_l1_g",
      "f",
      ["f", "e"],
      "LETTER_SHAPE",
    ),
    item(
      "ll.l1.t",
      "Jump on the letter g",
      "ll_l1_l",
      "g",
      ["g", "o"],
      "LETTER_SHAPE",
    ),
  ],
};

/* ── L2 · letter sounds ──────────────────────────────────────────────────────
 * Hear /m/, step to `m`. The step from shape to sound, which is the whole of
 * phonics and the reason the voice track is not optional decoration.
 *
 * ─── 🔴 THE PROMPTS NO LONGER ASK THE PHONEME, BY PRODUCT DECISION ───────────
 *
 * Every item below used to read *"Which letter says /m/?"* and now reads *"Jump
 * on the letter m"* — L1's sentence, verbatim, because the instruction was
 * that L1's method carries into L2 and L2's question style goes.
 *
 * ⚠️ AND THE SENTENCE NAMES NO SIDE, WHICH IS THE HALF THAT WAS WRONG. It read
 * *"Jump **right** on the letter m"* for a while, and "right" is a direction a
 * content row cannot know: `LagoonRound` shuffles the stones per round, so the
 * letter is on the left about half the time and the prompt was telling the child
 * to jump away from it. The round composes the direction now — see
 * `LagoonRound`'s guide sentence — and the authored line keeps the QUESTION.
 *
 * ⚠️ SO THE PROMPT NOW NAMES THE ANSWER, AND THIS LEVEL NO LONGER TESTS SOUND →
 * LETTER THROUGH ITS WORDS. That is the whole of the change and it is not a
 * side effect: a child who can hear "m" and find `m` is doing the same thing
 * here as at L1. What still separates the two levels is the item pool and the
 * `LETTER_SOUND` mode below, which is what the micro-step ladder (`steps.ts`
 * 2.1–2.4) selects on — the phoneme is still what the voice line is recorded
 * around, it is simply no longer the only route to the answer.
 *
 * ⚠️ `name` AND `mode` ARE LEFT ALONE ON PURPOSE. Both are structural — `mode`
 * is how `steps.ts` picks these items at all, and renaming the level reaches the
 * teacher dashboards. Changing either is a bigger edit than the one that was
 * asked for, and it should be made deliberately rather than as a consequence.
 */
const L2: Level = {
  level: 2,
  name: { en: "Hear the sound" },
  /*
   * 🔴 CHANGED WITH THE PROMPTS, BECAUSE IT DESCRIBED THEM. It read *"Jump on
   * the letter that makes the sound"*, which is no longer what any item asks —
   * an objective that survives the prompts it summarises is a line that quietly
   * becomes false. The method is now L1's, so this is L1's objective.
   */
  objective: {
    en: "Jump on the stone that matches",
  },
  ncf: ["C-9.3", "C-10.1"],
  /*
   * 🔴 SIX, LIKE EVERY LEVEL OF EVERY GAME. It was eight: L2 is where a child
   * stops matching shapes and starts hearing phonemes, the transition that most
   * rewards repetition inside a single sitting.
   *
   * ⚠️ *"Pools are ≥ 2× itemCount, so a repeat is not a replay"* — at 6 this
   * level needs twelve items, which the twenty-six below clear.
   */
  itemCount: 6,
  /*
   * 🔴 TWO, AND IT IS L1'S FLOOR — the founder's instruction that this level
   * show two ponds. `zones` is CONTENT: how many places there are to stand.
   * `LagoonRound` caps the row at two here (`maxStones`) and lays a home zone
   * between them (`centreHome`), so a declaration of four described a floor no
   * child stands on.
   *
   * ⚠️ AND THE STONE COUNT IS NOT THE LADDER'S ANY MORE AT THIS LEVEL, which is
   * the trade this makes and it is worth stating. `SUPPORT_RUNGS` trims the
   * haystack from four to three to two as a child struggles; at two there is
   * nothing left to trim. The ladder's other seven dials — Leo's patience, the
   * cue emphasis, the glow, Leo walking to the stone, Leo telling, target-only —
   * all still apply, and they are what carries support at L2 now.
   */
  motorAction: { kind: "step", zones: 2 },
  items: [
    item(
      "ll.l2.a",
      "Jump on the letter m",
      "ll_l2_a",
      "m",
      ["m", "s"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.b",
      "Jump on the letter s",
      "ll_l2_b",
      "s",
      ["s", "t"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.c",
      "Jump on the letter a",
      "ll_l2_c",
      "a",
      ["a", "k"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.d",
      "Jump on the letter t",
      "ll_l2_d",
      "t",
      ["t", "o"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.e",
      "Jump on the letter k",
      "ll_l2_e",
      "k",
      ["k", "a"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.f",
      "Jump on the letter f",
      "ll_l2_f",
      "f",
      ["f", "m"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.g",
      "Jump on the letter o",
      "ll_l2_g",
      "o",
      ["o", "s"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.h",
      "Jump on the letter l",
      "ll_l2_h",
      "l",
      ["l", "t"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.i",
      "Jump on the letter i",
      "ll_l2_i",
      "i",
      ["i", "k"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.j",
      "Jump on the letter r",
      "ll_l2_j",
      "r",
      ["r", "a"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.k",
      "Jump on the letter e",
      "ll_l2_k",
      "e",
      ["e", "l"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.l",
      "Jump on the letter g",
      "ll_l2_l",
      "g",
      ["g", "f"],
      "LETTER_SOUND",
    ),

    /*
     * 🔴 `b` — THE ONE LETTER THIS LEVEL COULD STILL GAIN, AND `c` IS THE ONE IT
     * CANNOT. L2 taught twelve of the fourteen letters `CUE_WORDS` covers; the
     * two it skipped were `b` and `c`, and they are not the same omission.
     *
     * `b` is simply added. Its confusables are `d`, `p` and `q` — the rule is
     * *"never co-present below level 3"* — and not one of the three exists
     * anywhere in this game's letter set, so the constraint is satisfied
     * structurally rather than by care while authoring.
     *
     * ⚠️ `c` IS DELIBERATELY ABSENT, AND IT IS A PHONEME CLASH RATHER THAN AN
     * OVERSIGHT — the same shape of decision as `o` and `i` at L3. This level
     * asks *"which letter says /k/?"*, and `c` says /k/ too. Admitting it would
     * produce two items with an identical **spoken** prompt and two different
     * right answers, which a child hears as the game changing its mind. `c` is
     * taught at L1 by shape and at L3 by `cup`, where the letter is named rather
     * than sounded and the ambiguity does not arise.
     */
    item(
      "ll.l2.u",
      "Jump on the letter b",
      "ll_l2_m",
      "b",
      ["b", "s"],
      "LETTER_SOUND",
    ),

    /*
     * ── the second half of the pool ─────────────────────────────────────────
     *
     * 🔴 THESE WERE THE 4-STONE VARIANTS, AND THIS LEVEL NO LONGER HAS STONE
     * COUNTS TO VARY. Every item here now offers two letters, because L2 lays
     * out two ponds — the founder's instruction, and `LagoonRound`'s `maxStones`
     * enforces it whatever the ladder asks for.
     *
     * ⚠️ WHICH LEAVES THEM DOING A DIFFERENT JOB, NOT NO JOB. A pool is *"≥ 2×
     * itemCount, so a repeat is not a replay"*, and thirteen items against an
     * itemCount of six only just clears that. What makes these thirteen worth keeping is
     * that each takes a DIFFERENT distractor from its authored set than its
     * twin above does: `m` is asked against `s` there and against `t` here, so
     * the pool holds twenty-six distinct discriminations rather than thirteen
     * questions listed twice. A child meeting `m` a second time in one sitting
     * meets a genuinely different pair.
     *
     * ⚠️ EVERY PAIR COMES FROM A SET THAT WAS ALREADY VETTED. Nothing here
     * invents a distractor — each is a letter the item was authored with — so
     * the `CONFUSABLE_PAIRS` rule this level is held to is satisfied by
     * construction rather than by re-checking twenty-six pairs by hand.
     *
     * ⚠️ THEIR IDS ARE NOT REUSED. `ll.l2.m`–`ll.l2.p` named two-stone questions
     * in every event already emitted; rebinding those slugs to new content would
     * silently merge two different questions in the metrics. The additions
     * continue from `u`.
     */
    item(
      "ll.l2.q",
      "Jump on the letter m",
      "ll_l2_a",
      "m",
      ["m", "t"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.r",
      "Jump on the letter k",
      "ll_l2_e",
      "k",
      ["k", "f"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.s",
      "Jump on the letter f",
      "ll_l2_f",
      "f",
      ["f", "r"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.t",
      "Jump on the letter r",
      "ll_l2_j",
      "r",
      ["r", "k"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.v",
      "Jump on the letter a",
      "ll_l2_c",
      "a",
      ["a", "l"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.w",
      "Jump on the letter t",
      "ll_l2_d",
      "t",
      ["t", "s"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.x",
      "Jump on the letter l",
      "ll_l2_h",
      "l",
      ["l", "a"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.y",
      "Jump on the letter e",
      "ll_l2_k",
      "e",
      ["e", "k"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.z",
      "Jump on the letter o",
      "ll_l2_g",
      "o",
      ["o", "t"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.aa",
      "Jump on the letter i",
      "ll_l2_i",
      "i",
      ["i", "e"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.ab",
      "Jump on the letter g",
      "ll_l2_l",
      "g",
      ["g", "b"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.ac",
      "Jump on the letter s",
      "ll_l2_b",
      "s",
      ["s", "f"],
      "LETTER_SOUND",
    ),
    item(
      "ll.l2.ad",
      "Jump on the letter b",
      "ll_l2_m",
      "b",
      ["b", "a"],
      "LETTER_SOUND",
    ),
  ],
};

/* ── L3 · beginning sounds ───────────────────────────────────────────────────
 * "Which one starts like mango?" The picture leads and the letter follows,
 * which is the last level before a child is asked to handle more than one
 * letter at a time.
 */
const L3: Level = {
  level: 3,
  /*
   * 🔴 THE LEVEL ASKS A DIFFERENT QUESTION NOW — *"what comes after a"*, on the
   * founder's instruction. It was `BEGINNING_SOUND`: *"which letter does mango
   * start with?"*, a third way of mapping a SOUND onto a shape after L1 matched
   * shapes and L2 matched sounds. This asks where a letter SITS, which is the
   * first thing in the game that cannot be answered from the card at all.
   *
   * ⚠️ AND THE ALPHABET STRIP ALONG THE BOTTOM IS WHAT MAKES IT ANSWERABLE
   * RATHER THAN A MEMORY TEST. A child who does not yet know the order can find
   * `f` on the chart and read across to `g` — which is what the chart on a
   * classroom wall is for, and is why it is drawn in the same capitals the
   * stones are. Without it this level would be asking a four-year-old to recite.
   *
   * ⚠️ `name` AND `objective` MOVED WITH THE PROMPTS, because they described
   * them. An objective that survives the questions it summarises is a line that
   * quietly becomes false — the same note L2's header makes.
   */
  name: { en: "What comes next?" },
  objective: {
    en: "Jump on the letter that comes next",
  },
  ncf: ["C-10.1", "C-10.2"],
  itemCount: 6,
  /* Identical to L1 and L2 — see the note there. */
  /*
   * 🔴 TWO, LIKE L1 AND L2 — the founder's instruction, *"make same in level
   * 3"*. Every level in this game now lays out two ponds with an island between
   * them, and `zones` is CONTENT: how many places there are to stand.
   *
   * ⚠️ AND THE ARGUMENT THAT USED TO EXEMPT L3 DESCRIBES CONTENT THIS GAME DOES
   * NOT HAVE. `lagoonSpec` §4c exempted it because *"its blending steps lay out
   * the letters of a word for the child to walk in order"* — a cap there removes
   * a LETTER OF THE WORD rather than a distractor. That is still true of
   * blending, and this level authors none of it: `Item.sequence` is set on
   * exactly zero items here. The exemption was protecting a level never built.
   *
   * ⚠️ `ll.l3.a` STILL CARRIES `b` AGAINST `d`, and it is the only confusable
   * pairing anywhere in this game — the rule is *"never co-present below level
   * 3"*, and this is level 3. It is no longer what the level is FOR (the
   * question is order now), but a pool that quietly stopped containing it would
   * have dropped the one place those two letters are ever seen together.
   */
  motorAction: { kind: "step", zones: 2 },
  items: [
    item(
      "ll.l3.a",
      "What comes after a?",
      "ll_l3_a",
      "b",
      ["b", "d"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.b",
      "What comes after b?",
      "ll_l3_b",
      "c",
      ["c", "t"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.c",
      "What comes after d?",
      "ll_l3_c",
      "e",
      ["e", "m"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.d",
      "What comes after e?",
      "ll_l3_d",
      "f",
      ["f", "s"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.e",
      "What comes after f?",
      "ll_l3_e",
      "g",
      ["g", "k"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.f",
      "What comes after h?",
      "ll_l3_f",
      "i",
      ["i", "o"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.g",
      "What comes after j?",
      "ll_l3_g",
      "k",
      ["k", "r"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.h",
      "What comes after k?",
      "ll_l3_h",
      "l",
      ["l", "a"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.i",
      "What comes after l?",
      "ll_l3_i",
      "m",
      ["m", "c"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.j",
      "What comes after n?",
      "ll_l3_j",
      "o",
      ["o", "f"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.k",
      "What comes after q?",
      "ll_l3_k",
      "r",
      ["r", "g"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.l",
      "What comes after r?",
      "ll_l3_l",
      "s",
      ["s", "i"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.m",
      "What comes after s?",
      "ll_l3_m",
      "t",
      ["t", "l"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.n",
      "What comes after a?",
      "ll_l3_n",
      "b",
      ["b", "s"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.o",
      "What comes after e?",
      "ll_l3_o",
      "f",
      ["f", "t"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.p",
      "What comes after f?",
      "ll_l3_p",
      "g",
      ["g", "m"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.q",
      "What comes after k?",
      "ll_l3_q",
      "l",
      ["l", "o"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.r",
      "What comes after l?",
      "ll_l3_r",
      "m",
      ["m", "b"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.s",
      "What comes after r?",
      "ll_l3_s",
      "s",
      ["s", "e"],
      "LETTER_ORDER",
    ),
    item(
      "ll.l3.t",
      "What comes after s?",
      "ll_l3_t",
      "t",
      ["t", "k"],
      "LETTER_ORDER",
    ),
  ],
};
export const LETTER_LAGOON: GameContent = {
  id: "letter-lagoon",
  category: "ALPHABETS_WORDS",
  contentSkill: Skill.LITERACY,
  /**
   * 🔴 BOTH, and unlike Bridge Patterns this game can back the claim: stepping
   * onto a stone and staying there is balance in a way a seated reach is not.
   * games-spec.md §5 names both explicitly.
   */
  motorSkills: [Skill.COORDINATION, Skill.BALANCE],
  emoji: "🔤",
  /**
   * 🔴 HIGH — this is one of the three games carrying a gross-motor core
   * (games-spec §1's three-of-five rule), so §3.6's play-zone prompt runs before
   * the first round: a child about to hop between stones needs room.
   */
  exertion: "high",
  levels: [L1, L2, L3],
};
